/**
 * Pure MCP client config snippets for the /docs portal generator.
 * No DOM — unit-tested.
 */

export type DocsSetupConfig = {
  publicOrigin: string;
  mcpResource: string;
  prmUrl: string;
  mcpClientId: string;
  mcpScope: string;
  cognitoIssuer: string;
  /** GitLab release page for the stdio PAT bridge (B13). */
  bridgeReleaseUrl: string;
};

export const DEFAULT_BRIDGE_RELEASE_URL =
  "https://gitlab.com/mcpto/ai/-/releases";

/** Claude Desktop / clients that accept url + optional headers. */
export function claudeDesktopOauthSnippet(cfg: DocsSetupConfig): string {
  return JSON.stringify(
    {
      mcpServers: {
        ai: {
          url: cfg.mcpResource,
        },
      },
    },
    null,
    2,
  );
}

/** Claude Desktop with PAT Bearer (fallback when OAuth discovery is unavailable). */
export function claudeDesktopPatSnippet(
  cfg: DocsSetupConfig,
  patPlaceholder = "ai_pat_REPLACE_ME",
): string {
  return JSON.stringify(
    {
      mcpServers: {
        ai: {
          url: cfg.mcpResource,
          headers: {
            Authorization: `Bearer ${patPlaceholder}`,
          },
        },
      },
    },
    null,
    2,
  );
}

/** Claude Code remote HTTP MCP (OAuth discovery preferred). */
export function claudeCodeOauthSnippet(cfg: DocsSetupConfig): string {
  return `claude mcp add --transport http ai ${cfg.mcpResource}`;
}

/** Claude Code / stdio clients via local PAT bridge (GitLab release asset). */
export function claudeCodeBridgeSnippet(
  cfg: DocsSetupConfig,
  patEnv = "INBOX_PAT",
): string {
  return [
    `# Download bridge from GitLab releases: ${cfg.bridgeReleaseUrl}`,
    `# export ${patEnv}=ai_pat_…   # mint in SPA; never commit`,
    `# export INBOX_MCP_URL=${cfg.mcpResource}`,
    `claude mcp add --transport stdio ai -- node /path/to/ai-stdio-bridge.mjs`,
  ].join("\n");
}

export function docsWizardSections(cfg: DocsSetupConfig): Array<{
  id: string;
  title: string;
  body: string[];
}> {
  const clientId =
    cfg.mcpClientId ||
    "(set VITE_MCP_CLIENT_ID / COGNITO_MCP_CLIENT_ID after deploy)";
  return [
    {
      id: "oauth",
      title: "OAuth discovery (preferred)",
      body: [
        `Resource URL: ${cfg.mcpResource}`,
        `PRM (RFC 9728): ${cfg.prmUrl}`,
        `Published MCP client_id (DCR returns this same id): ${clientId}`,
        `Cognito scope to request: openid ${cfg.mcpScope}`,
        cfg.cognitoIssuer
          ? `Authorization server: ${cfg.cognitoIssuer}`
          : "Authorization server: see PRM authorization_servers[0] after deploy",
        "Flow: unauthenticated POST /mcp → 401 + WWW-Authenticate resource_metadata → fetch PRM → Cognito authorize (PKCE S256) → Bearer access token on /mcp.",
        "SPA login cookies authenticate the control plane only — they do not authenticate /mcp.",
      ],
    },
    {
      id: "pat",
      title: "PAT fallback",
      body: [
        "Mint a PAT in the app (Tokens panel). Raw token shown once; format ai_pat_…",
        "Present as Authorization: Bearer ai_pat_… on https://mcpto.ai",
        "Scopes: read / send / full (enforced when pats.scopesEnforce is on). Prefer least privilege.",
        "Never paste PATs into chat, commits, or screenshots.",
      ],
    },
    {
      id: "attachments",
      title: "Attachments honesty",
      body: [
        "MCP upload_file: up to 50 MB decoded (content_base64 or file_url). Prefer file_url for very large remote objects.",
        "MCP get_file_content: default 2 MB return, max_bytes up to 50 MB.",
        "Vision image_base64: up to 20 MB total decoded per call.",
        "SPA / control-plane presigned PUT: up to 100 MB when attachments.presign is on (default on).",
        "MCP JSON-RPC body: up to 100 MB (base64 expands ~4/3 of binary size).",
      ],
    },
    {
      id: "bridge",
      title: "Stdio PAT bridge",
      body: [
        "For clients that only speak stdio MCP or cannot attach Authorization headers.",
        `Download: GitLab release asset + .sha256 (not npm) — ${cfg.bridgeReleaseUrl}`,
        "Env: INBOX_PAT, INBOX_MCP_URL (default https://mcpto.ai).",
        "Run: node ai-stdio-bridge.mjs (line-delimited JSON-RPC on stdio).",
        "Prefer native OAuth discovery or remote HTTP + headers when the client supports them.",
      ],
    },
    {
      id: "google",
      title: "Google Testing checklist",
      body: [
        "Google OAuth app remains External + Testing through Phase B — no leave-Testing, no Production-unverified, no CASA.",
        "Every Gmail user must be on the Google Cloud Console test-user list before Connect Gmail works.",
        "Refresh tokens expire about every 7 days under Testing — expect weekly Reconnect Gmail (needs_reauth status).",
        "Do not treat weekly reauth as pressure to leave Testing or start brand verification in B.",
      ],
    },
  ];
}
